import { Switch } from "@/components/ui/switch";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import {
  backofficeLinks,
  essentialMenuItem,
} from "@/components/lib/SidebarLinks";
import { Link, useParams, useNavigate } from "react-router-dom";
import { z } from "zod";
import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState, useMemo } from "react";
import useAuth from "@/hooks/useAuth";
import {
  createNewCategory,
  clearCategoryMessages,
} from "@/redux/slice/essential-menu-slice/category-slice";
import { useFetchAllMenuData } from "@/hooks/use-fetch-all-menu-data";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import CreateDrinksSkeleton from "@/components/skeleton/create-menu-skeleton";

const addNewCgtSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  parent_id: z.number().nullable().optional(),
  order: z
    .union([z.string(), z.number()])
    .transform((val) => (val === "" ? undefined : Number(val)))
    .refine((val) => val === undefined || Number.isInteger(val), {
      message: "View order must be an integer",
    })
    .optional(),
  status: z.enum(["active", "inactive", "archive", "draft"]).default("active"),
});

function EssentialAddNewCtg() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createLoading, getLoading, categories } = useSelector(
    (state) => state.essentialMenu.categories
  );
  const [showIsChaild, setShowIsChaild] = useState(false);

  const form = useForm({
    resolver: zodResolver(addNewCgtSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      order: "",
      menu_item_id: id,
      parent_id: null,
      status: "active",
    },
  });

  const fetchOnly = useMemo(() => ["categories"], []);

  const { groupLoading } = useFetchAllMenuData(user.restaurant_id, id, {
    fetchOnly,
  });

  const matchedItem = useMemo(() => {
    return essentialMenuItem.find((item) => item.id === Number(id));
  }, [id]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      menu_item_id: Number(id),
      client_id: user?.client_id,
      restaurant_id: user?.restaurant_id,
    };

    try {
      dispatch(clearCategoryMessages());
      const res = await dispatch(createNewCategory(payload));
      if (createNewCategory.fulfilled.match(res)) {
        toast.success("Category created successfully.");
        form.reset();
        dispatch(clearCategoryMessages());
        if (matchedItem) {
          navigate(`${matchedItem.path}/${id}`);
        } else {
          navigate("backoffice/menu/essentail-menu/drinks/1");
        }
      } else if (createNewCategory.rejected.match(res)) {
        toast.error(res?.payload);
      }
    } catch (error) {
      toast.error("Something went wrong.");
    }
  };

  if (getLoading || groupLoading) return <CreateDrinksSkeleton />;

  return (
    <Layout2
      pageName={{ title: "Add New Category" }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: `${matchedItem?.title}`, path: `${matchedItem?.path}/${id}` },
        {
          name: `Add New Category`,
          path: `${matchedItem?.path}/${id}/add-category`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <Card className="max-w-screen-md bg-cblack6 mx-auto mt-10 text-cwhite">
        <CardHeader>
          <CardTitle className="text-lg">Add New Category</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-7"
            >
              {/* Name */}
              <FormInputField
                name="name"
                style="bg-cblack7"
                placeholder="Name"
                form={form}
                type="text"
                label="Name"
              />

              {/* Description */}
              <FormInputField
                name="description"
                style="bg-cblack7"
                placeholder="Add note"
                form={form}
                type="text"
                label="Description"
              />

              <div className="space-y-4">
                <div className="flex gap-2 items-center">
                  <Switch
                    className="data-[state=unchecked]:bg-neutral-500"
                    checked={showIsChaild}
                    onCheckedChange={(val) => setShowIsChaild(val)}
                  />
                  <Label>Is Child</Label>
                </div>

                {showIsChaild && (
                  <FormInputField
                    name="parent_id"
                    style="bg-cblack7 flex-1"
                    placeholder="Select Category"
                    form={form}
                    type="select"
                    search={true}
                    parseAs="number"
                    selectOption={
                      categories?.length
                        ? categories.filter((item) => item?.status === "active").map((item) => ({
                            label: item?.name,
                            value: item?.id,
                          }))
                        : [
                            {
                              label: "No Menu available",
                              value: "",
                              disabled: true,
                            },
                          ]
                    }
                    label="Parent Category"
                  />
                )}
              </div>

              <FormInputField
                name="order"
                style="bg-cblack7"
                placeholder="0"
                form={form}
                type="text"
                label="View Order"
              />

              {/* Status */}
              <FormInputField
                name="status"
                style="bg-cblack7 h-10"
                placeholder="Select Status"
                form={form}
                type="select"
                selectOption={[
                  { label: "Select Status", value: "", disabled: true },
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
                label="Status"
              />

              {/* Buttons */}
              <div className="w-full flex gap-5 items-center justify-between mt-5">
                <Link className="w-full" to={`${matchedItem?.path}/${id}`}>
                  <Button className="bg-cblack7 w-full text-cwhite">
                    Cancel
                  </Button>
                </Link>
                <Button
                  isLoading={createLoading}
                  type="submit"
                  className="bg-cgreen text-white w-full"
                >
                  Publish
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </Layout2>
  );
}

export default EssentialAddNewCtg;
